import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import { InboxStackIcon } from '@heroicons/react/24/outline';

import { AddBoxDialog } from './AddBoxDialog';
import { CategoriesBox } from './CategoriesBox';
import { getStockages } from 'src/service/stockageApi';

// Palette de couleurs pour les points de stockage (10 couleurs)
const STOCKAGE_COLORS = [
  '#1b9faa', // Bleu-vert (couleur principale)
  '#ff6b6b', // Rouge
  '#4ecdc4', // Turquoise
  '#45b7d1', // Bleu clair
  '#96ceb4', // Vert menthe
  '#feca57', // Jaune
  '#ff9ff3', // Rose
  '#54a0ff', // Bleu
  '#5f27cd', // Violet
  '#00d2d3', // Cyan
];

// Récupérer les points de stockage depuis la variable d'environnement
const getStockagePoints = () => {
  try {
    return JSON.parse(process.env.REACT_APP_STOCKAGE_POINTS || '[]');
  } catch (error) {
    console.error('Erreur lors du parsing des points de stockage:', error);
    return [];
  }
};

  // Fonction pour obtenir la couleur d'un point de stockage
  const getStockageColor = (pointIndex) => {
    return STOCKAGE_COLORS[pointIndex % STOCKAGE_COLORS.length];
  };



export const Stockage = () => {
  const [stockage, setStockage] = useState([]);
  const [filteredStockage, setFilteredStockage] = useState([]);
  const [addBox, setAddBox] = useState();
  const [filterValue, setFilterValue] = useState(null);
  const [selectedStockagePoint, setSelectedStockagePoint] = useState(null);
  const history = useNavigate();
  
  const stockagePoints = getStockagePoints();

  // Fonction pour extraire le point de stockage du nom de la boîte
  const extractStockagePoint = (boxName) => {
    for (const point of stockagePoints) {
      if (boxName.includes(` - ${point}`)) {
        return point;
      }
    }
    return null;
  };

  useEffect(() => {
    getStockages("?motherBox=1").then((resp) => {
      resp.sort((a, b) => a.boxName.localeCompare(b.boxName))
      setStockage(resp);
      setFilteredStockage(resp);
    });
  }, [addBox]);

  const handleClick = id => {
    history("/dashboard/stockage/" + id);
  }

  const handleClickTransfer = () => {

  }

  const filterBox = () => {
    let filtered = stockage;
    
    // Filtre par texte de recherche
    if (filterValue) {
      filtered = filtered.filter((a) => {
        return a.boxName.toLowerCase().includes(filterValue.toLowerCase())
      });
    }
    
    // Filtre par point de stockage
    if (selectedStockagePoint) {
      filtered = filtered.filter((a) => {
        const boxStockagePoint = extractStockagePoint(a.boxName);
        return boxStockagePoint === selectedStockagePoint;
      });
    }
    
    setFilteredStockage(filtered);
  }

  useEffect(() => {
    filterBox();
  }, [filterValue, selectedStockagePoint, stockage]);

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold mb-8">Stockage</h1>
      
      {/* Boutons de filtrage par point de stockage */}
      {stockagePoints.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Points de stockage</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedStockagePoint(null)}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                selectedStockagePoint === null
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Tous
            </button>
            {stockagePoints.map((point, index) => (
              <button
                key={point}
                onClick={() => setSelectedStockagePoint(point)}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  selectedStockagePoint === point
                    ? 'text-white shadow-lg'
                    : 'text-white hover:opacity-80'
                }`}
                style={{
                  backgroundColor: selectedStockagePoint === point 
                    ? getStockageColor(index) 
                    : getStockageColor(index) + '80'
                }}
              >
                {point}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className="mb-4">
        <input
          type="text"
          placeholder="Recherche"
          value={filterValue || ''}
          onChange={(event) => setFilterValue(event.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <AddBoxDialog 
        buttonText="+ Boîte" 
        addBox={addBox} 
        setAddBox={setAddBox} 
        stockagePoints={stockagePoints}
        getStockageColor={getStockageColor}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-8">
        {filteredStockage.map((data) => {
          const stockedObjects = (data.annonces) ? data.annonces.filter((a) => {
            return a.status === 'TROUVÉ'
          }) : [];
          
          // Déterminer la couleur en fonction du point de stockage
          const boxStockagePoint = extractStockagePoint(data.boxName);
          const stockagePointIndex = stockagePoints.indexOf(boxStockagePoint);
          const boxColor = stockagePointIndex >= 0 ? getStockageColor(stockagePointIndex) : '#1b9faa';
        
          return (
            <div key={data.id} className="flex flex-col items-center">
              <div 
                onClick={() => handleClick(data.id)}
                className="relative cursor-pointer group"
              >
                <div className="relative">
                  <InboxStackIcon 
                    className="w-24 h-24 group-hover:opacity-80 transition-opacity"
                    style={{ color: boxColor }}
                  />
                  {stockedObjects.length > 0 && (
                    <span 
                      className="absolute top-3 left-1/2 transform -translate-x-1/2 text-xl font-bold"
                      style={{ color: boxColor }}
                    >
                      {stockedObjects.length}
                    </span>
                  )}
                </div>
              </div>
              <span className="mt-2 text-sm text-gray-600 text-center">
                {boxStockagePoint ? data.boxName.replace(` - ${boxStockagePoint}`, '') : data.boxName}
              </span>
              {boxStockagePoint && (
                <span 
                  className="mt-1 text-xs font-medium px-2 py-1 rounded-full text-white"
                  style={{ backgroundColor: boxColor }}
                >
                  {boxStockagePoint}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Stockage;
